import React, { useEffect, useMemo, useState } from 'react'
import './AdminPage.css'

type AdminRecordType = 'creation' | 'contact_request'

type AdminRecord = {
  type: AdminRecordType
  id: string
  createdAt: string
  email?: string
  name?: string
  phone?: string
  comment?: string
  consentAssist?: boolean | null
  modelName?: string
  layerCount?: number | null
  hasCreation?: boolean
  creationLinkAvailable?: boolean
  sourceFile?: string
}

type AdminStats = {
  total: number
  creations: number
  contactRequests: number
  returned: number
  limit: number
}

type AdminRecordsResponse = {
  stats?: Partial<AdminStats>
  records?: AdminRecord[]
}

type FilterType = 'all' | AdminRecordType

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').trim().replace(/\/$/, '')
const toApiUrl = (routePath: string) => (API_BASE_URL ? `${API_BASE_URL}${routePath}` : routePath)
const API_ROUTE_BASE = '/api_indyannastudio'

const ADMIN_TOKEN_STORAGE_KEY = 'indyanna_admin_token'
const ADMIN_TOKEN_EXPIRY_STORAGE_KEY = 'indyanna_admin_token_expiry'

const EMPTY_STATS: AdminStats = {
  total: 0,
  creations: 0,
  contactRequests: 0,
  returned: 0,
  limit: 300,
}

const toSafeString = (value: unknown) => String(value || '').trim()

const formatDateParis = (isoDate: string) => {
  const asDate = new Date(isoDate)
  if (Number.isNaN(asDate.getTime())) return isoDate
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(asDate)
}

const getTypeLabel = (type: AdminRecordType) => (type === 'creation' ? 'Creation' : 'Contact')

const getConsentLabel = (value: boolean | null | undefined) => {
  if (value === true) return 'Oui'
  if (value === false) return 'Non'
  return '-'
}

const getStoredToken = () => {
  const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || ''
  const expiryRaw = sessionStorage.getItem(ADMIN_TOKEN_EXPIRY_STORAGE_KEY) || ''
  const expiryMs = Date.parse(expiryRaw)
  if (token && Number.isFinite(expiryMs) && expiryMs > 0 && expiryMs <= Date.now()) {
    sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
    sessionStorage.removeItem(ADMIN_TOKEN_EXPIRY_STORAGE_KEY)
    return ''
  }
  return token
}

function AdminPage() {
  const [password, setPassword] = useState('')
  const [token, setToken] = useState(() => getStoredToken())
  const [records, setRecords] = useState<AdminRecord[]>([])
  const [stats, setStats] = useState<AdminStats>(EMPTY_STATS)
  const [limit, setLimit] = useState(300)
  const [filter, setFilter] = useState<FilterType>('all')
  const [isLoading, setIsLoading] = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshAt, setLastRefreshAt] = useState<string>('')

  const clearSession = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
    sessionStorage.removeItem(ADMIN_TOKEN_EXPIRY_STORAGE_KEY)
    setToken('')
    setRecords([])
    setStats(EMPTY_STATS)
  }

  const fetchRecords = async (sessionToken: string, customLimit = limit) => {
    const cleanToken = toSafeString(sessionToken)
    if (!cleanToken) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch(
        toApiUrl(`${API_ROUTE_BASE}/admin/records?limit=${encodeURIComponent(String(customLimit))}`),
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${cleanToken}`,
          },
          cache: 'no-store',
        }
      )
      if (response.status === 401) {
        clearSession()
        setError('Session expiree. Reconnectez-vous.')
        return
      }
      const payload = (await response.json().catch(() => ({}))) as AdminRecordsResponse & {
        error?: string
      }
      if (!response.ok) {
        throw new Error(payload.error || 'Impossible de charger les donnees admin')
      }

      const nextRecords = Array.isArray(payload.records) ? payload.records : []
      setRecords(nextRecords)
      setStats({
        total: Number(payload.stats?.total || 0),
        creations: Number(payload.stats?.creations || 0),
        contactRequests: Number(payload.stats?.contactRequests || 0),
        returned: Number(payload.stats?.returned || nextRecords.length),
        limit: Number(payload.stats?.limit || customLimit || 300),
      })
      setLastRefreshAt(new Date().toISOString())
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Erreur inconnue')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!token) return
    void fetchRecords(token, limit)
  }, [token, limit])

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const cleanPassword = password.trim()
    if (!cleanPassword) {
      setError('Mot de passe requis.')
      return
    }
    setIsAuthenticating(true)
    setError(null)
    try {
      const response = await fetch(toApiUrl(`${API_ROUTE_BASE}/admin/login`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: cleanPassword }),
      })
      const payload = (await response.json().catch(() => ({}))) as {
        token?: string
        expiresAt?: string
        error?: string
      }
      if (!response.ok) {
        throw new Error(payload.error || 'Connexion admin impossible')
      }
      const nextToken = toSafeString(payload.token)
      if (!nextToken) {
        throw new Error('Token admin invalide')
      }
      sessionStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, nextToken)
      if (toSafeString(payload.expiresAt)) {
        sessionStorage.setItem(ADMIN_TOKEN_EXPIRY_STORAGE_KEY, toSafeString(payload.expiresAt))
      } else {
        sessionStorage.removeItem(ADMIN_TOKEN_EXPIRY_STORAGE_KEY)
      }
      setToken(nextToken)
      setPassword('')
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Erreur de connexion')
    } finally {
      setIsAuthenticating(false)
    }
  }

  const handleLogout = async () => {
    if (token) {
      try {
        await fetch(toApiUrl(`${API_ROUTE_BASE}/admin/logout`), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      } catch {
        // Ignore logout transport errors; local logout still applies.
      }
    }
    clearSession()
    setError(null)
  }

  const handleOpenSavedFile = async (record: AdminRecord) => {
    if (!token) {
      setError('Session admin absente. Reconnectez-vous.')
      return
    }

    try {
      const response = await fetch(
        toApiUrl(
          `${API_ROUTE_BASE}/admin/open-link?type=${encodeURIComponent(record.type)}&id=${encodeURIComponent(record.id)}&mode=editor&baseUrl=${encodeURIComponent(window.location.origin)}`
        ),
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cache: 'no-store',
        }
      )

      if (response.status === 401) {
        clearSession()
        throw new Error('Session expiree. Reconnectez-vous.')
      }
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string }
        throw new Error(payload.error || 'Fichier de sauvegarde indisponible')
      }

      const payload = (await response.json().catch(() => ({}))) as { openUrl?: string; error?: string }
      const openUrl = toSafeString(payload.openUrl)
      if (!openUrl) {
        throw new Error(payload.error || 'Lien de creation introuvable')
      }
      window.open(openUrl, '_blank', 'noopener,noreferrer')
      setError(null)
    } catch (openError) {
      setError(openError instanceof Error ? openError.message : 'Erreur lors de l ouverture du fichier')
    }
  }

  const filteredRecords = useMemo(() => {
    if (filter === 'all') return records
    return records.filter((item) => item.type === filter)
  }, [records, filter])

  return (
    <main className="admin-page">
      <section className="admin-card">
        <header className="admin-header">
          <h1>Administration des creations</h1>
          <p>Vue securisee par mot de passe (verifie cote serveur). Horaires en heure de Paris.</p>
        </header>

        {!token ? (
          <form className="admin-login-form" onSubmit={handleLogin}>
            <label htmlFor="admin-password">Mot de passe admin</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Votre mot de passe"
              autoComplete="current-password"
            />
            <button type="submit" disabled={isAuthenticating}>
              {isAuthenticating ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        ) : (
          <>
            <div className="admin-toolbar">
              <div className="admin-toolbar-left">
                <label>
                  Type
                  <select value={filter} onChange={(event) => setFilter(event.target.value as FilterType)}>
                    <option value="all">Tous</option>
                    <option value="creation">Creations</option>
                    <option value="contact_request">Demandes contact</option>
                  </select>
                </label>
                <label>
                  Limite
                  <select
                    value={String(limit)}
                    onChange={(event) => setLimit(Number(event.target.value) || 300)}
                  >
                    <option value="100">100</option>
                    <option value="300">300</option>
                    <option value="500">500</option>
                    <option value="1000">1000</option>
                  </select>
                </label>
              </div>
              <div className="admin-toolbar-right">
                <button type="button" onClick={() => void fetchRecords(token, limit)} disabled={isLoading}>
                  {isLoading ? 'Chargement...' : 'Rafraichir'}
                </button>
                <button type="button" className="secondary" onClick={handleLogout}>
                  Deconnexion
                </button>
              </div>
            </div>

            <div className="admin-stats">
              <span>Total: {stats.total}</span>
              <span>Creations: {stats.creations}</span>
              <span>Contacts: {stats.contactRequests}</span>
              <span>Affiches: {filteredRecords.length}</span>
              {lastRefreshAt && <span>Dernier refresh: {formatDateParis(lastRefreshAt)}</span>}
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date/heure</th>
                    <th>Type</th>
                    <th>Email</th>
                    <th>Nom</th>
                    <th>Telephone</th>
                    <th>Commentaire</th>
                    <th>Modele</th>
                    <th>Calques</th>
                    <th>Consentement</th>
                    <th>Sauvegarde</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => {
                    const canOpenSavedFile = Boolean(record.creationLinkAvailable)
                    return (
                      <tr key={`${record.type}-${record.id}-${record.createdAt}`}>
                        <td>{formatDateParis(record.createdAt)}</td>
                        <td>{getTypeLabel(record.type)}</td>
                        <td>{toSafeString(record.email) || '-'}</td>
                        <td>{toSafeString(record.name) || '-'}</td>
                        <td>{toSafeString(record.phone) || '-'}</td>
                        <td>{toSafeString(record.comment) || '-'}</td>
                        <td>{toSafeString(record.modelName) || '-'}</td>
                        <td>
                          {typeof record.layerCount === 'number' && Number.isFinite(record.layerCount)
                            ? record.layerCount
                            : '-'}
                        </td>
                        <td>{getConsentLabel(record.consentAssist)}</td>
                        <td>
                          <button
                            type="button"
                            className="admin-open-file-btn"
                            onClick={() => void handleOpenSavedFile(record)}
                            disabled={!canOpenSavedFile}
                          >
                            Ouvrir
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                  {filteredRecords.length === 0 && (
                    <tr>
                      <td colSpan={10} className="admin-empty-row">
                        Aucune entree trouvee.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {error && <p className="admin-error">{error}</p>}
      </section>
    </main>
  )
}

export default AdminPage
